import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { ProgramFeatures } from 'src/app/core-module/models/UserManagement/program-features';
import { UserSettingsPermissionsResult } from 'src/app/core-module/models/UserManagement/user-settings-permissions-result';
import { MenuOption } from 'src/app/core-module/models/Utils/menu-option';
import { MenuOptionHolder } from 'src/app/core-module/models/Utils/menu-option-holder';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { AuthService } from 'src/app/core-module/services/auth.service';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { UserManagementService } from 'src/app/core-module/services/usermanagement.service';
import { CustomerContextService } from 'src/app/core-module/services/customer-context.service';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SettingsComponent {
  private readonly customerContext = inject(CustomerContextService);
  constructor(private userManagement: UserManagementService, private router: Router, private toast: ToastrService, private auth: AuthService) {
    this.getFeaturePermissions();
    this.buildProfileOptions();
    this.buildUsersOptions();
    this.buildLocationMenuOptions();
    this.buildAuditMenuOptions();
    this.buildEventsOptions();
    this.buildCustomerSupportOptions();
    this.buildExternalAuditOptions();
    this.userManagement.userSettingsPermissions().pipe(
      tap((res: UserSettingsPermissionsResult) => {
        if (!res.IsError) {
          this.permissions = new UserSettingsPermissionsResult(res);
        }
      })
    ).subscribe();
  }

  public Profile: MenuOptionHolder = new MenuOptionHolder({ 'SectionName': 'Profile' })
  public UsersMenu: MenuOptionHolder = new MenuOptionHolder({ 'SectionName': 'Users' })
  public LocationsMenu: MenuOptionHolder = new MenuOptionHolder({ 'SectionName': 'Locations' })
  public AuditsMenu: MenuOptionHolder = new MenuOptionHolder({ 'SectionName': this.customerContext.displayText('Audits') })
  public EventsMenu: MenuOptionHolder = new MenuOptionHolder({ 'SectionName': 'Events' })
  public CustomerSupportMenu: MenuOptionHolder = new MenuOptionHolder({ 'SectionName': 'Support' })
  public ExternalAuditsMenu: MenuOptionHolder = new MenuOptionHolder({ 'SectionName': 'Feedback Campaigns' })
  public permissions: UserSettingsPermissionsResult = new UserSettingsPermissionsResult();
  public hasCampaignPermission: boolean = false;
  public hasEventPermission: boolean = false;

  manageLicenses() {
    this.userManagement.manageLicenses().pipe(
      tap((res: string) => {
        if (res.length > 0) {
          window.location.href = res;
        } else {
          this.toast.addToastr(new Toastr({ Type: 3, Message: "An error occurred getting your billing information, please try again later." }));
        }

      })
    ).subscribe();
  }
  buildUsersOptions() {
    this.UsersMenu.Options.push(new MenuOption({ 'URL': 'settings/users/newuserwizard', 'MenuName': 'New User', HelpText: "Create a new user" }));
    this.UsersMenu.Options.push(new MenuOption({ 'URL': 'settings/users/edit', 'MenuName': 'User Edit', HelpText: "Edit an existing user" }));


  }
  buildProfileOptions() {
    this.Profile.Options.push(new MenuOption({ 'URL': 'settings/users/profile', 'MenuName': 'Update Profile', HelpText: "Update your account details" }));
  }
  buildLocationMenuOptions() {
    this.LocationsMenu.Options.push(new MenuOption({ 'URL': '/settings/locations/newlocation', 'MenuName': 'New Location', HelpText: "Create a new location" }));
    this.LocationsMenu.Options.push(new MenuOption({ 'URL': '/settings/locations/editlocation', 'MenuName': 'Edit Locations', HelpText: "Edit details of locations" }));
    this.LocationsMenu.Options.push(new MenuOption({ 'URL': '/settings/locations/assignlocationaudits', 'MenuName': this.customerContext.displayText('Location Audits'), HelpText: this.customerContext.displayText("Assign audits to locations") }));
    this.LocationsMenu.Options.push(new MenuOption({ 'URL': 'settings/locations/assignlocations', 'MenuName': 'Location Users', HelpText: "Assign users to locations" }));
    this.LocationsMenu.Options.push(new MenuOption({ 'URL': 'settings/users/existing', 'MenuName': 'Location User Permissions', HelpText: "Assign user permissions by location" }));
  }
  buildAuditMenuOptions() {
    this.AuditsMenu.Options.push(new MenuOption({ 'URL': '/settings/manage/new/audit', 'MenuName': this.customerContext.displayText('New Audit'), HelpText: this.customerContext.displayText("Create a new audit") }));
    this.AuditsMenu.Options.push(new MenuOption({ 'URL': '/settings/manage/edit', 'MenuName': this.customerContext.displayText('Edit Existing Audit'), HelpText: this.customerContext.displayText("Edit an existing audit") }));
    this.AuditsMenu.Options.push(new MenuOption({ 'URL': '/settings/manage/audit/locations', 'MenuName': this.customerContext.displayText('Audit Locations'), HelpText: this.customerContext.displayText("Assign locations to audits") }));
    this.AuditsMenu.Options.push(new MenuOption({ 'URL': '/settings/locations/assignuseraudits', 'MenuName': this.customerContext.displayText('Audit Users'), HelpText: this.customerContext.displayText("Assign users to audits") }));
  }
  buildEventsOptions() {
    this.EventsMenu.Options.push(new MenuOption({ 'URL': 'settings/events/workflows', 'MenuName': 'Workflows', HelpText: "Create and manage reusable event workflows" }));
    this.EventsMenu.Options.push(new MenuOption({ 'URL': 'settings/events/templates', 'MenuName': 'Event Templates', HelpText: "Configure event templates and location availability" }));
  }
  buildCustomerSupportOptions() {
    this.CustomerSupportMenu.Options.push(new MenuOption({ 'URL': 'settings/customersupport', 'MenuName': 'Contact Support', HelpText: "Send technical support requests" }));
    this.CustomerSupportMenu.Options.push(new MenuOption({ 'URL': 'settings/retrieveaudit', 'MenuName': this.customerContext.displayText('Retrieve Audit'), HelpText: this.customerContext.displayText("Retrieve lost audits, contact support first to arrange") }));
  }
  buildExternalAuditOptions() {
    this.ExternalAuditsMenu.Options.push(new MenuOption({ 'URL': 'settings/campaigns/newcampaign', 'MenuName': 'Create Feedback Campaign', HelpText: "Create a new feedback campaign" }));
    this.ExternalAuditsMenu.Options.push(new MenuOption({ 'URL': 'settings/campaigns/campaignlist', 'MenuName': 'Feedback Campaigns', HelpText: "View active, upcoming and completed feedback campaigns" }));
  }
  getFeaturePermissions() {
    this.auth.getFeaturePermissions().pipe(
      tap((res: ProgramFeatures) => {
        if (!res.IsError) {
          this.hasCampaignPermission = res.Campaigns;
          this.hasEventPermission = res.Events;
        }
      })
    ).subscribe();
  }

}
